import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Plot from '../../PlotlyWrapper';
import {
  fetchAdAnomalyTypes,
  fetchAdTelemetryAnomalyDevices,
  fetchAdTelemetryDeviceEvents,
  fetchAdTelemetryDeviceHistogram,
  fetchAdTelemetryFleetOverview,
} from '../api/restapi';
import { formatLocalDateTime, formatLocalDateTimeWithZone } from '../utils/formatDateTime';
import '../assets/ADTelemetry.css';
import '../assets/FleetAnalytics.css';

const AD_DURATIONS = [
    { key: '7d',  label: 'D' },
    { key: '14d', label: 'W' },
    { key: '30d', label: 'M' },
];

const PLOTLY_CONFIG = { displayModeBar: false, responsive: true };
const FONT_BASE = { family: "'Inter', system-ui, sans-serif", size: 11, color: '#4b5563' };
const LAYOUT_BASE = { paper_bgcolor: 'rgba(0,0,0,0)', plot_bgcolor: 'rgba(0,0,0,0)', font: FONT_BASE };
const CHART_COLORS = ['#1a56db', '#7c3aed', '#0d9488', '#d97706', '#dc2626', '#16a34a', '#0891b2'];
const RANGE_DAYS = { '7d': 7, '14d': 14, '30d': 30 };
const PAGE_SIZE = 50;
const LOGGING_LABELS = {
  LOGGING_ENABLED: 'Logging Enabled',
  DETECTION_ONLY: 'Detection Only',
};
const LOGGING_COLORS = { LOGGING_ENABLED: '#16a34a', DETECTION_ONLY: '#d97706' };
const numberFormat = new Intl.NumberFormat();

const formatNumber = (value) => numberFormat.format(Number(value) || 0);
const formatPercent = (value) => value === null || value === undefined ? '—' : `${Number(value).toFixed(1)}%`;
const formatLoggingState = (state) => LOGGING_LABELS[state] || state || 'Detection Only';
const labelForAnomaly = (item) => `${item.adId ?? item.anomalyId} · ${item.anomalyName || 'Unnamed anomaly'}`;

const formatMtba = (seconds) => {
  if (seconds === null || seconds === undefined) return 'Unavailable';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${(seconds / 60).toFixed(1)}m`;
  if (seconds < 86400) return `${(seconds / 3600).toFixed(1)}h`;
  return `${(seconds / 86400).toFixed(1)}d`;
};

const buildWindow = (timeRange, refreshAt) => {
  const end = new Date(refreshAt);
  const start = new Date(end.getTime() - RANGE_DAYS[timeRange] * 24 * 60 * 60 * 1000);
  return { startTime: start.toISOString(), endTime: end.toISOString() };
};

const utcDaysInWindow = (startTime, endTime) => {
  const days = [];
  const current = new Date(startTime);
  current.setUTCHours(0, 0, 0, 0);
  const end = new Date(endTime);
  while (current < end) {
    days.push(current.toISOString().slice(0, 10));
    current.setUTCDate(current.getUTCDate() + 1);
  }
  return days;
};

const exportCsv = (rows, filename) => {
  const csv = rows.map((row) => row.map((value) => `"${String(value ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
  const anchor = document.createElement('a');
  anchor.href = `data:text/csv;charset=utf-8,${encodeURIComponent(csv)}`;
  anchor.download = filename;
  anchor.click();
};

function ChartContent({ loading, error, hasData, emptyMessage, children }) {
  if (loading) return <div className="chart-state">Loading telemetry…</div>;
  if (error) return <div className="chart-state chart-state-error">{error}</div>;
  if (!hasData) return <div className="chart-state">{emptyMessage || 'No telemetry matches the selected filters.'}</div>;
  return children;
}

function Pagination({ page, hasNext, onPrevious, onNext }) {
  return (
    <div className="table-pagination">
      <span>Page {page + 1}</span>
      <button type="button" className="page-btn" disabled={page === 0} onClick={onPrevious}>Previous</button>
      <button type="button" className="page-btn" disabled={!hasNext} onClick={onNext}>Next</button>
    </div>
  );
}

function ADTelemetry({ onNavigateToConfigurations }) {
  const [timeRange, setTimeRange] = useState('14d');
  const [technology, setTechnology] = useState('');
  const [platform, setPlatform] = useState('');
  const [refreshAt, setRefreshAt] = useState(() => Date.now());

  const [catalog, setCatalog] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [catalogError, setCatalogError] = useState(null);
  const [fleet, setFleet] = useState(null);
  const [fleetLoading, setFleetLoading] = useState(true);
  const [fleetError, setFleetError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [colorBy, setColorBy] = useState('technology');

  const [selectedAnomalyId, setSelectedAnomalyId] = useState('');
  const [anomalyDevices, setAnomalyDevices] = useState(null);
  const [anomalyDevicesLoading, setAnomalyDevicesLoading] = useState(false);
  const [anomalyDevicesError, setAnomalyDevicesError] = useState(null);
  const [deviceSearch, setDeviceSearch] = useState('');
  const [deviceLoggingState, setDeviceLoggingState] = useState('');
  const [devicePage, setDevicePage] = useState(0);

  const [systemIdInput, setSystemIdInput] = useState('');
  const [selectedSystemId, setSelectedSystemId] = useState('');
  const [histogram, setHistogram] = useState(null);
  const [histogramLoading, setHistogramLoading] = useState(false);
  const [histogramError, setHistogramError] = useState(null);
  const [events, setEvents] = useState(null);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsError, setEventsError] = useState(null);
  const [eventSearch, setEventSearch] = useState('');
  const [eventLoggingState, setEventLoggingState] = useState('');
  const [eventAnomalyId, setEventAnomalyId] = useState('');
  const [eventPage, setEventPage] = useState(0);
  const [expandedEvents, setExpandedEvents] = useState(new Set());

  const window = useMemo(() => buildWindow(timeRange, refreshAt), [timeRange, refreshAt]);
  const filters = useMemo(() => ({
    ...window,
    technology: technology || undefined,
    platform: platform || undefined,
  }), [window, technology, platform]);
  const rangeLabel = `Last ${RANGE_DAYS[timeRange]} Days`;

  const technologyOptions = useMemo(() => Array.from(new Set(catalog.map((item) => item.technology).filter(Boolean))).sort(), [catalog]);
  const platformOptions = useMemo(() => Array.from(new Set([
    ...catalog.map((item) => item.platform),
    ...(fleet?.platformDistribution || []).map((item) => item.platform),
  ].filter((item) => item && item !== 'UNRESOLVED'))).sort(), [catalog, fleet]);
  const anomalyById = useMemo(() => new Map(catalog.map((item) => [String(item.adId), item])), [catalog]);
  const selectedAnomaly = anomalyById.get(String(selectedAnomalyId));

  useEffect(() => {
    let active = true;
    setCatalogLoading(true);
    setCatalogError(null);
    fetchAdAnomalyTypes().then((result) => {
      if (!active) return;
      if (result?.errorMessage) {
        setCatalog([]);
        setCatalogError(result.errorMessage);
      } else {
        setCatalog(Array.isArray(result) ? result : []);
      }
      setCatalogLoading(false);
    });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    let active = true;
    setFleetLoading(true);
    setFleetError(null);
    fetchAdTelemetryFleetOverview(filters).then((result) => {
      if (!active) return;
      if (result?.errorMessage) {
        setFleet(null);
        setFleetError(result.errorMessage);
      } else {
        setFleet(result);
        setLastUpdated(new Date());
      }
      setFleetLoading(false);
    });
    return () => { active = false; };
  }, [filters]);

  useEffect(() => {
    if (selectedAnomalyId || !catalog.length) return;
    const topAnomalyId = fleet?.mostFrequent?.adId ?? fleet?.mostFrequent?.anomalyId;
    const matchingTopAnomaly = catalog.find((item) => String(item.adId) === String(topAnomalyId));
    setSelectedAnomalyId(String((matchingTopAnomaly || catalog[0]).adId));
  }, [catalog, fleet, selectedAnomalyId]);

  useEffect(() => {
    setDevicePage(0);
    setEventPage(0);
  }, [timeRange, technology, platform]);

  useEffect(() => {
    if (!selectedAnomalyId) {
      setAnomalyDevices(null);
      return undefined;
    }
    let active = true;
    setAnomalyDevicesLoading(true);
    setAnomalyDevicesError(null);
    fetchAdTelemetryAnomalyDevices(selectedAnomalyId, {
      ...filters,
      search: deviceSearch || undefined,
      loggingState: deviceLoggingState || undefined,
      page: devicePage,
      pageSize: PAGE_SIZE,
    }).then((result) => {
      if (!active) return;
      if (result?.errorMessage) {
        setAnomalyDevices(null);
        setAnomalyDevicesError(result.errorMessage);
      } else {
        setAnomalyDevices(result);
      }
      setAnomalyDevicesLoading(false);
    });
    return () => { active = false; };
  }, [selectedAnomalyId, filters, deviceSearch, deviceLoggingState, devicePage]);

  useEffect(() => {
    if (!selectedSystemId) {
      setHistogram(null);
      setEvents(null);
      return undefined;
    }
    let active = true;
    setHistogramLoading(true);
    setHistogramError(null);
    fetchAdTelemetryDeviceHistogram(selectedSystemId, filters).then((result) => {
      if (!active) return;
      if (result?.errorMessage) {
        setHistogram(null);
        setHistogramError(result.errorMessage);
      } else {
        setHistogram(result);
      }
      setHistogramLoading(false);
    });
    return () => { active = false; };
  }, [selectedSystemId, filters]);

  useEffect(() => {
    if (!selectedSystemId) return undefined;
    let active = true;
    setEventsLoading(true);
    setEventsError(null);
    setExpandedEvents(new Set());
    fetchAdTelemetryDeviceEvents(selectedSystemId, {
      ...filters,
      anomalyId: eventAnomalyId || undefined,
      search: eventSearch || undefined,
      loggingState: eventLoggingState || undefined,
      page: eventPage,
      pageSize: PAGE_SIZE,
    }).then((result) => {
      if (!active) return;
      if (result?.errorMessage) {
        setEvents(null);
        setEventsError(result.errorMessage);
      } else {
        setEvents(result);
      }
      setEventsLoading(false);
    });
    return () => { active = false; };
  }, [selectedSystemId, filters, eventAnomalyId, eventSearch, eventLoggingState, eventPage]);

  const mainBar = useMemo(() => {
    const rows = [...(fleet?.topAnomalies || [])].reverse();
    const labels = rows.map(labelForAnomaly);
    const common = { y: labels, type: 'bar', orientation: 'h', customdata: rows.map((item) => item.anomalyId) };
    let traces;
    if (colorBy === 'technology') {
      const technologies = Array.from(new Set(rows.map((item) => item.technology || 'Unresolved')));
      traces = technologies.map((currentTechnology, index) => ({
        ...common,
        name: currentTechnology,
        x: rows.map((item) => item.technology === currentTechnology || (!item.technology && currentTechnology === 'Unresolved') ? item.occurrenceCount : 0),
        marker: { color: CHART_COLORS[index % CHART_COLORS.length] },
        hovertemplate: `<b>%{y}</b><br>${currentTechnology}: %{x}<extra></extra>`,
      }));
    } else {
      traces = [
        { ...common, name: 'Logging Enabled', x: rows.map((item) => item.loggingEnabledCount), marker: { color: '#16a34a' }, hovertemplate: '<b>%{y}</b><br>Logging enabled: %{x}<extra></extra>' },
        { ...common, name: 'Detection Only', x: rows.map((item) => item.detectionOnlyCount), marker: { color: '#d97706' }, hovertemplate: '<b>%{y}</b><br>Detection only: %{x}<extra></extra>' },
      ];
    }
    return {
      traces,
      layout: {
        ...LAYOUT_BASE, barmode: 'stack', height: Math.max(300, rows.length * 35 + 70),
        xaxis: { title: 'Occurrence Count', gridcolor: '#f3f4f6', zeroline: false },
        yaxis: { automargin: true, tickfont: { size: 11 } },
        margin: { t: 10, r: 20, b: 50, l: 190 }, legend: { orientation: 'h', x: 0, y: -0.18 },
      },
    };
  }, [fleet, colorBy]);

  const trend = useMemo(() => {
    const rows = fleet?.dailyTrend || [];
    const days = utcDaysInWindow(window.startTime, window.endTime);
    const groups = new Map();
    rows.forEach((item) => {
      const key = String(item.anomalyId);
      if (!groups.has(key)) groups.set(key, { label: labelForAnomaly(item), counts: new Map() });
      groups.get(key).counts.set(item.day, item.occurrenceCount);
    });
    return {
      traces: Array.from(groups.values()).map((group, index) => ({
        name: group.label, x: days, y: days.map((day) => group.counts.get(day) || 0), type: 'scatter', mode: 'lines+markers',
        line: { color: CHART_COLORS[index % CHART_COLORS.length], width: 2 }, marker: { size: 4 },
      })),
      layout: {
        ...LAYOUT_BASE, height: 265,
        xaxis: { gridcolor: '#f3f4f6', tickangle: -30, tickfont: { size: 10 } },
        yaxis: { title: 'Count', gridcolor: '#f3f4f6', zeroline: false },
        margin: { t: 10, r: 15, b: 68, l: 55 }, legend: { orientation: 'h', x: 0, y: -0.34, font: { size: 10 } },
      },
    };
  }, [fleet, window]);

  const loggingSplit = useMemo(() => {
    const rows = fleet?.loggingSplit || [];
    return {
      traces: [{
        labels: rows.map((item) => formatLoggingState(item.state)), values: rows.map((item) => item.occurrenceCount), type: 'pie', hole: 0.45,
        marker: { colors: rows.map((item) => LOGGING_COLORS[item.state] || '#6b7280') }, textinfo: 'percent', hoverinfo: 'label+value+percent', textfont: { size: 10 },
      }],
      layout: { ...LAYOUT_BASE, height: 230, showlegend: true, legend: { orientation: 'v', font: { size: 10 }, x: 1, y: 0.5 }, margin: { t: 10, r: 130, b: 10, l: 10 } },
    };
  }, [fleet]);

  const platformDistribution = useMemo(() => {
    const rows = fleet?.platformDistribution || [];
    return {
      traces: [{
        labels: rows.map((item) => item.platform || 'Unresolved'), values: rows.map((item) => item.occurrenceCount), type: 'pie', hole: 0.45,
        marker: { colors: rows.map((_, index) => CHART_COLORS[index % CHART_COLORS.length]) }, textinfo: 'percent', hoverinfo: 'label+value+percent', textfont: { size: 10 },
      }],
      layout: { ...LAYOUT_BASE, height: 230, showlegend: true, legend: { orientation: 'v', font: { size: 10 }, x: 1, y: 0.5 }, margin: { t: 10, r: 115, b: 10, l: 10 } },
    };
  }, [fleet]);

  const topDevices = useMemo(() => {
    const rows = [...(fleet?.topDevices || [])].reverse();
    return {
      traces: [{
        x: rows.map((item) => item.occurrenceCount), y: rows.map((item) => item.systemId), type: 'bar', orientation: 'h', customdata: rows.map((item) => item.systemId),
        marker: { color: '#1a56db' }, hovertemplate: '<b>%{y}</b><br>Count: %{x}<extra></extra>',
      }],
      layout: {
        ...LAYOUT_BASE, height: Math.max(260, rows.length * 30 + 60),
        xaxis: { title: 'Total Anomaly Count', gridcolor: '#f3f4f6', zeroline: false }, yaxis: { automargin: true, tickfont: { size: 11 } }, margin: { t: 10, r: 20, b: 50, l: 130 },
      },
    };
  }, [fleet]);

  const heatmap = useMemo(() => {
    const rows = fleet?.heatmap || [];
    const days = utcDaysInWindow(window.startTime, window.endTime);
    const groups = new Map();
    rows.forEach((item) => {
      const key = String(item.anomalyId);
      if (!groups.has(key)) groups.set(key, { label: labelForAnomaly(item), counts: new Map() });
      groups.get(key).counts.set(item.day, item.occurrenceCount);
    });
    const anomalies = Array.from(groups.values());
    return {
      traces: [{
        z: anomalies.map((item) => days.map((day) => item.counts.get(day) || 0)), x: days, y: anomalies.map((item) => item.label), type: 'heatmap',
        colorscale: [[0, '#e8f0fe'], [0.5, '#1a56db'], [1, '#1e429f']], showscale: true, hovertemplate: '<b>%{y}</b> on %{x}<br>Count: %{z}<extra></extra>',
      }],
      layout: {
        ...LAYOUT_BASE, height: Math.max(280, anomalies.length * 28 + 80),
        xaxis: { tickangle: -35, tickfont: { size: 10 } }, yaxis: { automargin: true, tickfont: { size: 11 } }, margin: { t: 10, r: 80, b: 65, l: 190 },
      },
    };
  }, [fleet, window]);

  const deviceDistribution = useMemo(() => {
    const rows = histogram?.distribution || [];
    return {
      traces: [{ x: rows.map(labelForAnomaly), y: rows.map((item) => item.occurrenceCount), type: 'bar', marker: { color: '#1a56db' }, hovertemplate: '<b>%{x}</b><br>Count: %{y}<extra></extra>' }],
      layout: {
        ...LAYOUT_BASE, height: 280,
        xaxis: { tickangle: -35, tickfont: { size: 10 }, gridcolor: '#f3f4f6' }, yaxis: { title: 'Count', gridcolor: '#f3f4f6', zeroline: false }, margin: { t: 10, r: 15, b: 95, l: 55 },
      },
    };
  }, [histogram]);

  const newVsRecurring = useMemo(() => {
    const rows = histogram?.newVsRecurring || [];
    return {
      traces: [
        { name: 'Recurring', x: rows.map((item) => item.day), y: rows.map((item) => item.recurringOccurrenceCount), type: 'bar', marker: { color: '#1a56db' } },
        { name: 'New', x: rows.map((item) => item.day), y: rows.map((item) => item.newOccurrenceCount), type: 'bar', marker: { color: '#0d9488' } },
      ],
      layout: {
        ...LAYOUT_BASE, barmode: 'stack', height: 280,
        xaxis: { tickangle: -30, tickfont: { size: 10 }, gridcolor: '#f3f4f6' }, yaxis: { title: 'Anomaly Count', gridcolor: '#f3f4f6', zeroline: false },
        legend: { orientation: 'h', x: 0, y: -0.3, font: { size: 10 } }, margin: { t: 10, r: 15, b: 70, l: 55 },
      },
    };
  }, [histogram]);

  const coOccurrence = useMemo(() => {
    const rows = histogram?.coOccurrence || [];
    const ids = Array.from(new Set(rows.flatMap((item) => [String(item.anomalyIdLeft), String(item.anomalyIdRight)])));
    const labels = ids.map((id) => {
      const item = anomalyById.get(id);
      return item ? `${item.adId} · ${item.name || 'Unnamed anomaly'}` : `AD ${id}`;
    });
    const counts = new Map(rows.map((item) => [`${item.anomalyIdLeft}:${item.anomalyIdRight}`, item.pairCount]));
    return {
      traces: [{
        z: ids.map((left) => ids.map((right) => left === right ? 0 : counts.get(`${left}:${right}`) || counts.get(`${right}:${left}`) || 0)),
        x: labels, y: labels, type: 'heatmap', colorscale: [[0, '#f9fafb'], [0.5, '#bfdbfe'], [1, '#1a56db']], showscale: true,
        hovertemplate: '<b>%{y}</b> + <b>%{x}</b><br>Co-occurrences: %{z}<extra></extra>',
      }],
      layout: {
        ...LAYOUT_BASE, height: Math.max(300, ids.length * 34 + 80),
        xaxis: { tickangle: -35, tickfont: { size: 10 }, automargin: true }, yaxis: { automargin: true, tickfont: { size: 10 } }, margin: { t: 10, r: 80, b: 115, l: 170 },
      },
    };
  }, [histogram, anomalyById]);

  const configurationByAnomalyId = useMemo(() => {
    const grouped = new Map();
    (events?.configuration || []).forEach((item) => {
      const key = String(item.anomalyId);
      if (!grouped.has(key)) grouped.set(key, []);
      grouped.get(key).push(item);
    });
    return grouped;
  }, [events]);

  const selectAnomalyFromChart = useCallback((data) => {
    const anomalyId = data?.points?.[0]?.customdata;
    if (anomalyId === null || anomalyId === undefined) return;
    setSelectedAnomalyId(String(anomalyId));
    setDevicePage(0);
    setTimeout(() => document.getElementById('ad-tel-sec2')?.scrollIntoView({ behavior: 'smooth' }), 50);
  }, []);

  const selectDevice = useCallback((systemId) => {
    if (!systemId) return;
    setSystemIdInput(systemId);
    setSelectedSystemId(systemId);
    setEventPage(0);
    setTimeout(() => document.getElementById('ad-tel-sec3')?.scrollIntoView({ behavior: 'smooth' }), 50);
  }, []);

  const selectDeviceFromChart = useCallback((data) => selectDevice(data?.points?.[0]?.customdata), [selectDevice]);
  const toggleExpandedEvent = useCallback((key) => {
    setExpandedEvents((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);
  const copyLogPath = useCallback(async (path) => {
    if (!path || !navigator.clipboard) return;
    try { await navigator.clipboard.writeText(path); } catch (_) { /* Browser permission failures are non-blocking. */ }
  }, []);

  const deviceRows = anomalyDevices?.devices || [];
  const eventRows = events?.events || [];
  const fleetHasData = Boolean(fleet && fleet.summary.totalAnomalies > 0);
  const selectedDeviceLoaded = Boolean(selectedSystemId);

  return (
    <div className="ad-telemetry-scope">
      <div className="page">

        {/* Unified filter bar — same pattern as MBB Telemetry */}
        <div className="fho-dashboard" style={{ marginBottom: 0 }}>
          <div className="page-intro">
            <div className="duration-bar">
              <span className="duration-label">Duration:</span>
              {AD_DURATIONS.map(d => (
                <span
                  key={d.key}
                  className={`filter-chip${timeRange === d.key ? ' active' : ''}`}
                  onClick={() => setTimeRange(d.key)}
                >
                  {d.label}
                </span>
              ))}
              <span className="duration-divider" />
              <span className="duration-label">Technology:</span>
              <span className={`filter-chip${!technology ? ' active' : ''}`} onClick={() => setTechnology('')}>Any</span>
              {technologyOptions.map((option) => (
                <span key={option} className={`filter-chip${technology === option ? ' active' : ''}`} onClick={() => setTechnology(option)}>{option}</span>
              ))}
              <span className="duration-divider" />
              <span className="duration-label">Platform:</span>
              <select
                id="ad-platform"
                value={platform}
                onChange={(event) => setPlatform(event.target.value)}
                style={{ fontSize: '12px', padding: '5px 10px', border: '1.5px solid #e2e8f0', borderRadius: '20px', background: '#fff', color: '#374151', cursor: 'pointer', outline: 'none', fontFamily: 'inherit' }}
              >
                <option value="">All</option>
                {platformOptions.map((option) => <option key={option} value={option}>{option}</option>)}
              </select>
              <span className="duration-divider" />
              <span
                className="filter-chip"
                onClick={() => setRefreshAt(Date.now())}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}
              >
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="23 4 23 10 17 10" /><path d="M20.5 16a9 9 0 1 1-1.4-8.6" /></svg>
                Refresh
              </span>
              {lastUpdated && (
                <span className="duration-label" style={{ marginLeft: '4px' }}>
                  {lastUpdated.toISOString().slice(0, 16).replace('T', ' ')} UTC
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="section-divider sec-anchor" id="ad-tel-sec1"><div className="sec-num">1</div><h2>Fleet-Level Anomaly Overview</h2><span className="sec-desc">Aggregate occurrence counts across all devices — {rangeLabel}</span></div>
        {fleetError && <div className="status-banner status-banner-error">{fleetError}</div>}
        <div className={`kpi-grid${fleetLoading ? ' loading-content' : ''}`}>
          <div className="kpi-card"><div className="kpi-label">Total Anomalies</div><div className="kpi-value">{fleetLoading ? '…' : formatNumber(fleet?.summary.totalAnomalies)}</div><div className="kpi-sub">Selected period</div></div>
          <div className="kpi-card kpi-green"><div className="kpi-label">Devices Affected</div><div className="kpi-value">{fleetLoading ? '…' : formatNumber(fleet?.summary.devicesAffected)}</div><div className="kpi-sub">Distinct systems</div></div>
          <div className="kpi-card kpi-amber"><div className="kpi-label">Most Frequent</div><div className="kpi-value kpi-name">{fleetLoading ? '…' : (fleet?.mostFrequent?.anomalyName || '—')}</div><div className="kpi-sub">{fleet?.mostFrequent ? `${fleet.mostFrequent.adId ?? fleet.mostFrequent.anomalyId} · ${formatNumber(fleet.mostFrequent.occurrenceCount)} events` : 'No matching anomaly'}</div></div>
          <div className="kpi-card kpi-purple"><div className="kpi-label">Logging Enabled</div><div className="kpi-value">{fleetLoading ? '…' : formatPercent(fleet?.summary.loggingEnabledPercent)}</div><div className="kpi-sub">Events with logging required</div></div>
          <div className="kpi-card kpi-red"><div className="kpi-label">Escalation Rate</div><div className="kpi-value kpi-name">{fleetLoading ? '…' : formatPercent(fleet?.summary.escalationRatePercent)}</div><div className="kpi-sub" title={fleet?.summary.escalationRateReason || undefined}>{fleet?.summary.escalationRateReason ? 'Unavailable in client telemetry' : 'Pre-trigger to main-trigger'}</div></div>
        </div>

        <div className="card"><div className="card-hdr"><h3>Top Anomalies by Occurrence Count</h3><div className="card-actions"><label>Color by:</label><div className="seg"><button type="button" className={`seg-btn${colorBy === 'technology' ? ' active' : ''}`} onClick={() => setColorBy('technology')}>Technology</button><button type="button" className={`seg-btn${colorBy === 'logging' ? ' active' : ''}`} onClick={() => setColorBy('logging')}>Logging Status</button></div></div></div><div className="card-body chart-body-wide"><ChartContent loading={fleetLoading} error={fleetError} hasData={fleetHasData}><Plot data={mainBar.traces} layout={mainBar.layout} config={PLOTLY_CONFIG} style={{ width: '100%' }} onClick={selectAnomalyFromChart} /></ChartContent></div></div>

        <div className="chart-grid chart-grid-23">
          <div className="chart-card"><div className="ch-hdr"><h4>Daily Anomaly Trend — Top 5</h4></div><div className="ch-body"><ChartContent loading={fleetLoading} error={fleetError} hasData={Boolean(fleet?.dailyTrend?.length)}><Plot data={trend.traces} layout={trend.layout} config={PLOTLY_CONFIG} style={{ width: '100%' }} /></ChartContent></div></div>
          <div className="chart-card"><div className="ch-hdr"><h4>Detection Only vs Logging Enabled</h4></div><div className="ch-body"><ChartContent loading={fleetLoading} error={fleetError} hasData={Boolean(fleet?.loggingSplit?.some((item) => item.occurrenceCount))}><Plot data={loggingSplit.traces} layout={loggingSplit.layout} config={PLOTLY_CONFIG} style={{ width: '100%' }} /></ChartContent></div></div>
        </div>
        <div className="chart-grid chart-grid-2">
          <div className="chart-card"><div className="ch-hdr"><h4>Top 10 Devices by Total Anomaly Count</h4><span className="chart-hint">Select a bar to view the device</span></div><div className="ch-body"><ChartContent loading={fleetLoading} error={fleetError} hasData={Boolean(fleet?.topDevices?.length)}><Plot data={topDevices.traces} layout={topDevices.layout} config={PLOTLY_CONFIG} style={{ width: '100%' }} onClick={selectDeviceFromChart} /></ChartContent></div></div>
          <div className="chart-card"><div className="ch-hdr"><h4>Platform / Product Distribution</h4></div><div className="ch-body"><ChartContent loading={fleetLoading} error={fleetError} hasData={Boolean(fleet?.platformDistribution?.some((item) => item.occurrenceCount))}><Plot data={platformDistribution.traces} layout={platformDistribution.layout} config={PLOTLY_CONFIG} style={{ width: '100%' }} /></ChartContent></div></div>
        </div>
        <div className="chart-card heatmap-card"><div className="ch-hdr"><h4>Anomaly × Day Heatmap — Top 10 Anomalies</h4><span className="chart-hint">Color intensity = occurrence count</span></div><div className="ch-body"><ChartContent loading={fleetLoading} error={fleetError} hasData={Boolean(fleet?.heatmap?.length)}><Plot data={heatmap.traces} layout={heatmap.layout} config={PLOTLY_CONFIG} style={{ width: '100%' }} /></ChartContent></div></div>

        <div className="section-divider sec-anchor" id="ad-tel-sec2"><div className="sec-num">2</div><h2>Anomaly Drill-Down — Devices Affected</h2><span className="sec-desc">Select a catalog anomaly to review its impacted systems</span></div>
        {catalogError && <div className="status-banner status-banner-error">{catalogError}</div>}
        <div className="ctrl-bar compact-ctrl-bar"><div className="ctrl-group"><label htmlFor="ad-anomaly-picker">Anomaly</label><select id="ad-anomaly-picker" className="sel anomaly-picker" value={selectedAnomalyId} disabled={catalogLoading || !catalog.length} onChange={(event) => { setSelectedAnomalyId(event.target.value); setDevicePage(0); }}>{catalogLoading && <option>Loading catalog…</option>}{!catalogLoading && !catalog.length && <option>No anomaly types available</option>}{catalog.map((item) => <option key={item.adId} value={String(item.adId)}>{item.adId} · {item.name || 'Unnamed anomaly'}</option>)}</select></div></div>
        <div className="card"><div className="card-hdr"><h3>Devices affected by {selectedAnomaly ? `${selectedAnomaly.name || 'Unnamed anomaly'} (${selectedAnomaly.adId})` : 'the selected anomaly'} — {rangeLabel}</h3></div><div className="card-body">
          <div className={`kpi-grid kpi-grid-4${anomalyDevicesLoading ? ' loading-content' : ''}`}>
            <div className="kpi-card"><div className="kpi-label">Distinct Devices</div><div className="kpi-value">{anomalyDevicesLoading ? '…' : formatNumber(anomalyDevices?.summary.distinctDevices)}</div><div className="kpi-sub">Affected this period</div></div>
            <div className="kpi-card kpi-amber"><div className="kpi-label">Total Occurrences</div><div className="kpi-value">{anomalyDevicesLoading ? '…' : formatNumber(anomalyDevices?.summary.totalOccurrences)}</div><div className="kpi-sub">Across all systems</div></div>
            <div className="kpi-card kpi-green"><div className="kpi-label">Avg. per Device</div><div className="kpi-value">{anomalyDevicesLoading ? '…' : (anomalyDevices?.summary.averagePerDevice || 0).toFixed(1)}</div><div className="kpi-sub">Occurrences per system</div></div>
            <div className="kpi-card kpi-purple"><div className="kpi-label">MTBA</div><div className="kpi-value kpi-name">{anomalyDevicesLoading ? '…' : formatMtba(anomalyDevices?.summary.mtbaSeconds)}</div><div className="kpi-sub">Mean time between anomalies</div></div>
          </div>
          {anomalyDevicesError && <div className="status-banner status-banner-error">{anomalyDevicesError}</div>}
          <div className="tbar"><input type="search" className="inp-sm" placeholder="Search system or device ID…" value={deviceSearch} onChange={(event) => { setDeviceSearch(event.target.value); setDevicePage(0); }} /><select className="sel" value={deviceLoggingState} onChange={(event) => { setDeviceLoggingState(event.target.value); setDevicePage(0); }}><option value="">All Logging States</option>{Object.entries(LOGGING_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select><button type="button" className="export-btn" disabled={!deviceRows.length} onClick={() => exportCsv([['System ID', 'Device ID', 'Technology', 'Platform', 'Occurrences', 'Last Occurrence', 'Logging Status'], ...deviceRows.map((item) => [item.systemId, item.deviceId, item.technology, item.platform, item.occurrenceCount, item.lastOccurrence, formatLoggingState(item.loggingState)])], 'ad-anomaly-devices.csv')}>Export CSV</button></div>
          <div className="table-wrap"><table className="view-table"><thead><tr><th>System ID</th><th>Device ID</th><th>Technology</th><th>Platform</th><th>Occurrences</th><th>Last Occurrence</th><th>Logging Status</th><th>Action</th></tr></thead><tbody>
            {!anomalyDevicesLoading && !anomalyDevicesError && !deviceRows.length && <tr><td colSpan="8" className="table-empty">No devices match the selected filters.</td></tr>}
            {deviceRows.map((item) => <tr key={item.systemId}><td><button type="button" className="view-link button-link" onClick={() => selectDevice(item.systemId)}>{item.systemId}</button></td><td>{item.deviceId || '—'}</td><td>{item.technology ? <span className="badge badge-blue">{item.technology}</span> : '—'}</td><td>{item.platform || '—'}</td><td><strong>{formatNumber(item.occurrenceCount)}</strong></td><td>{formatLocalDateTime(item.lastOccurrence) || '—'}</td><td><span className={`badge ${item.loggingState === 'LOGGING_ENABLED' ? 'badge-green' : 'badge-amber'}`}>{formatLoggingState(item.loggingState)}</span></td><td><button type="button" className="view-link button-link" onClick={() => selectDevice(item.systemId)}>View timeline →</button></td></tr>)}
          </tbody></table></div>
          <Pagination page={devicePage} hasNext={deviceRows.length === PAGE_SIZE} onPrevious={() => setDevicePage((page) => Math.max(0, page - 1))} onNext={() => setDevicePage((page) => page + 1)} />
        </div></div>

        <div className="section-divider sec-anchor" id="ad-tel-sec3"><div className="sec-num">3</div><h2>Device-Level Anomaly Histogram</h2><span className="sec-desc">Load a system ID to inspect its anomaly distribution</span></div>
        <div className="ctrl-bar compact-ctrl-bar"><div className="ctrl-group"><label htmlFor="ad-system-id">System ID</label><input id="ad-system-id" type="text" className="sel input-select" value={systemIdInput} onChange={(event) => setSystemIdInput(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') selectDevice(systemIdInput.trim()); }} placeholder="Enter system ID" /></div><button type="button" className="refresh-btn" disabled={!systemIdInput.trim()} onClick={() => selectDevice(systemIdInput.trim())}>Load Device</button></div>
        {!selectedDeviceLoaded && <div className="status-banner">Enter a system ID or select one from the device charts above to load Sections 3 and 4.</div>}
        {histogramError && <div className="status-banner status-banner-error">{histogramError}</div>}
        {selectedDeviceLoaded && <>
          <div className={`kpi-grid${histogramLoading ? ' loading-content' : ''}`}>
            <div className="kpi-card"><div className="kpi-label">Total Anomalies</div><div className="kpi-value">{histogramLoading ? '…' : formatNumber(histogram?.summary.totalAnomalies)}</div><div className="kpi-sub">This system, this period</div></div>
            <div className="kpi-card kpi-amber"><div className="kpi-label">Distinct Types Hit</div><div className="kpi-value">{histogramLoading ? '…' : formatNumber(histogram?.summary.distinctTypesHit)}</div><div className="kpi-sub">Anomaly types</div></div>
            <div className="kpi-card kpi-green"><div className="kpi-label">Most Frequent</div><div className="kpi-value kpi-name">{histogramLoading ? '…' : (histogram?.mostFrequent?.anomalyName || '—')}</div><div className="kpi-sub">{histogram?.mostFrequent ? `${histogram.mostFrequent.adId ?? histogram.mostFrequent.anomalyId} · ${formatNumber(histogram.mostFrequent.occurrenceCount)} events` : 'No matching anomaly'}</div></div>
            <div className="kpi-card"><div className="kpi-label">New Anomalies</div><div className="kpi-value">{histogramLoading ? '…' : formatNumber(histogram?.newAnomalies.anomalyTypeCount)}</div><div className="kpi-sub">Not seen in the prior equivalent window</div></div>
            <div className="kpi-card kpi-purple"><div className="kpi-label">Recurring</div><div className="kpi-value">{histogramLoading ? '…' : formatNumber(histogram?.recurring.occurrenceCount)}</div><div className="kpi-sub">Occurrences of previously seen types</div></div>
          </div>
          <div className="chart-grid chart-grid-2"><div className="chart-card"><div className="ch-hdr"><h4>Anomaly Distribution — {selectedSystemId}</h4></div><div className="ch-body"><ChartContent loading={histogramLoading} error={histogramError} hasData={Boolean(histogram?.distribution?.length)}><Plot data={deviceDistribution.traces} layout={deviceDistribution.layout} config={PLOTLY_CONFIG} style={{ width: '100%' }} /></ChartContent></div></div><div className="chart-card"><div className="ch-hdr"><h4>New vs Recurring Over Time</h4></div><div className="ch-body"><ChartContent loading={histogramLoading} error={histogramError} hasData={Boolean(histogram?.newVsRecurring?.length)}><Plot data={newVsRecurring.traces} layout={newVsRecurring.layout} config={PLOTLY_CONFIG} style={{ width: '100%' }} /></ChartContent></div></div></div>
          <div className="chart-card heatmap-card"><div className="ch-hdr"><h4>Co-occurrence Matrix — Anomaly Pairs within 60s</h4><span className="chart-hint">Color intensity = pair count</span></div><div className="ch-body"><ChartContent loading={histogramLoading} error={histogramError} hasData={Boolean(histogram?.coOccurrence?.length)}><Plot data={coOccurrence.traces} layout={coOccurrence.layout} config={PLOTLY_CONFIG} style={{ width: '100%' }} /></ChartContent></div></div>
        </>}

        <div className="section-divider sec-anchor" id="ad-tel-sec4"><div className="sec-num">4</div><h2>Device-Level Anomaly Detail Table</h2><span className="sec-desc">Telemetry events, collection evidence, and current AD configuration metadata</span></div>
        <div className="card"><div className="card-hdr"><h3>Anomaly Events — {selectedSystemId || 'select a system ID'}</h3></div><div className="card-body">
          {!selectedDeviceLoaded && <div className="chart-state">Load a system in Section 3 to view its event detail.</div>}
          {selectedDeviceLoaded && <>
            {eventsError && <div className="status-banner status-banner-error">{eventsError}</div>}
            <div className="tbar"><input type="search" className="inp-sm" placeholder="Search anomaly name or ID…" value={eventSearch} onChange={(event) => { setEventSearch(event.target.value); setEventPage(0); }} /><select className="sel" value={eventAnomalyId} onChange={(event) => { setEventAnomalyId(event.target.value); setEventPage(0); }}><option value="">All Anomalies</option>{catalog.map((item) => <option key={item.adId} value={String(item.adId)}>{item.adId} · {item.name || 'Unnamed anomaly'}</option>)}</select><select className="sel" value={eventLoggingState} onChange={(event) => { setEventLoggingState(event.target.value); setEventPage(0); }}><option value="">All Logging States</option>{Object.entries(LOGGING_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select><button type="button" className="export-btn" disabled={!eventRows.length} onClick={() => exportCsv([['Anomaly Detected Timestamp', 'Anomaly Name', 'AD ID', 'Logging State', 'Merged ODL S3 Keys'], ...eventRows.map((item) => [formatLocalDateTimeWithZone(item.eventTimestamp), item.anomalyName, item.anomalyId, formatLoggingState(item.loggingState), item.artifacts.map((artifact) => artifact.s3Uri).filter(Boolean).join(' | ') || 'Unavailable'])], 'ad-device-events.csv')}>Export CSV</button></div>
            <div className="table-wrap"><table className="view-table"><thead><tr><th></th><th>Anomaly Detected Timestamp</th><th>Anomaly Name</th><th>AD ID</th><th>Logging State</th><th>Merged ODL Artifact</th><th>Configuration</th></tr></thead><tbody>
              {!eventsLoading && !eventsError && !eventRows.length && <tr><td colSpan="7" className="table-empty">No anomaly events match the selected filters.</td></tr>}
              {eventRows.map((item, index) => {
                const key = `${item.eventTimestamp}-${item.anomalyId}-${index}`;
                const configuration = configurationByAnomalyId.get(String(item.anomalyId)) || [];
                const resolvedConfigIds = new Set(item.artifacts.flatMap((artifact) => artifact.configurationIds));
                return <React.Fragment key={key}><tr><td><button type="button" className="expand-button" aria-label="Toggle collection and configuration details" onClick={() => toggleExpandedEvent(key)}>{expandedEvents.has(key) ? '▼' : '▶'}</button></td><td className="nowrap">{formatLocalDateTimeWithZone(item.eventTimestamp) || '—'}</td><td><strong>{item.anomalyName || '—'}</strong></td><td><span className="badge badge-gray">{item.anomalyId}</span></td><td><span className={`badge ${item.loggingState === 'LOGGING_ENABLED' ? 'badge-green' : 'badge-amber'}`}>{formatLoggingState(item.loggingState)}</span></td><td>{item.artifacts.some((artifact) => artifact.s3Uri) ? <button type="button" className="view-link button-link" onClick={() => toggleExpandedEvent(key)}>{item.artifacts.filter((artifact) => artifact.s3Uri).length} key{item.artifacts.filter((artifact) => artifact.s3Uri).length === 1 ? '' : 's'}</button> : <span className="unavailable-value">Unavailable</span>}</td><td>{configuration.length ? <><span className="badge badge-teal">{configuration.length} handle{configuration.length === 1 ? '' : 's'}</span>{item.loggingState === 'DETECTION_ONLY' && <button type="button" className="view-link button-link" onClick={() => onNavigateToConfigurations?.(item.anomalyId)}>View configurations</button>}</> : <button type="button" className="view-link button-link" onClick={() => onNavigateToConfigurations?.(item.anomalyId)}>View configurations</button>}</td></tr>
                  {expandedEvents.has(key) && <tr className="exp-row"><td colSpan="7">{item.artifacts.length > 0 && <><strong>Merged ODL artifacts</strong><div className="configuration-list">{item.artifacts.map((artifact) => <div className="configuration-item" key={artifact.mergeJobId}><span><b>Chunk:</b> {artifact.mergeSequence || '—'}</span><span><b>Merge status:</b> {artifact.mergeStatus || 'Unavailable'}</span><span><b>S3 key:</b> {artifact.s3Uri ? <button type="button" className="view-link button-link" onClick={() => copyLogPath(artifact.s3Uri)}>Copy {artifact.s3Uri}</button> : 'Unavailable'}</span><span><b>Configuration resolution:</b> {artifact.configurationResolutionStatus || 'Unavailable'}</span></div>)}</div></>}<strong>Current configuration metadata</strong>{configuration.length ? <div className="configuration-list">{configuration.map((config, configIndex) => <div className="configuration-item" key={`${config.configurationId}-${config.handleId}-${configIndex}`}><span><b>Config:</b> {config.label || config.configurationId || 'Unavailable'} {resolvedConfigIds.has(String(config.configurationId)) && <span className="badge badge-green">Resolved for artifact</span>}</span><span><b>Status:</b> {config.status || 'Unavailable'}</span><span><b>Technology:</b> {config.technology || 'Unavailable'}</span><span><b>Platform:</b> {config.platform || 'Unavailable'}</span><span><b>Handle:</b> {config.handleId || 'Unavailable'}</span><span><b>Trigger:</b> {[config.triggerGroup, config.triggerClass, config.triggerName].filter(Boolean).join(' · ') || 'Unavailable'}</span><span><b>Class values:</b> {config.classValues || 'Unavailable'}</span></div>)}</div> : <span> No current configuration metadata is available for this anomaly. <button type="button" className="view-link button-link" onClick={() => onNavigateToConfigurations?.(item.anomalyId)}>View configurations</button></span>}</td></tr>}
                </React.Fragment>;
              })}
            </tbody></table></div>
            <Pagination page={eventPage} hasNext={eventRows.length === PAGE_SIZE} onPrevious={() => setEventPage((page) => Math.max(0, page - 1))} onNext={() => setEventPage((page) => page + 1)} />
          </>}
        </div></div>
      </div>
    </div>
  );
}

export default ADTelemetry;
